import { createFileRoute } from "@tanstack/react-router";
import { claimAudio, registerAudioOwner, type AudioOwner } from "@/lib/audio-bus";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useRef, useState } from "react";
import { Eye, Headphones, Hand, Waves, Play, Pause, Shield } from "lucide-react";
import { VolumeControl } from "@/components/VolumeControl";
import { getAudioContext, resumeAudio, unlockAudio } from "@/lib/audio-context";

export const Route = createFileRoute("/bilateral")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Bilateral Stimulation — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Self-guided bilateral stimulation tools — visual, audio, and butterfly tapping. Educational wellness, not therapy.",
      },
      { property: "og:title", content: "Bilateral Stimulation — Recovery Trauma™" },
      { property: "og:description", content: "Self-guided bilateral stimulation tools — visual, audio, and butterfly tapping. Educational wellness, not therapy." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/bilateral" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/bilateral" }],
  }),
  component: withMemberGate(BilateralPage, "Bilateral Stimulation"),
});

type Mode = "visual" | "audio" | "butterfly" | "soundscape";

const MODES: { id: Mode; label: string; blurb: string; icon: typeof Eye }[] = [
  { id: "visual", label: "Visual", blurb: "Watch a soft dot move side to side.", icon: Eye },
  { id: "audio", label: "Audio", blurb: "Gentle alternating tones, left ↔ right.", icon: Headphones },
  { id: "butterfly", label: "Butterfly Tap", blurb: "A guided self-tapping rhythm.", icon: Hand },
  { id: "soundscape", label: "Soundscape", blurb: "Bilateral ambient soundscape.", icon: Waves },
];

const VOLUME_KEY = "rt.bilateral.volume";
const MUTED_KEY = "rt.bilateral.muted";

function BilateralPage() {
  const [mode, setMode] = useState<Mode>("visual");
  const [playing, setPlaying] = useState(false);
  const [speedMs, setSpeedMs] = useState(900); // half-period
  const ownerRef = useRef<AudioOwner>({ stop: () => setPlaying(false) });
  useEffect(() => registerAudioOwner(ownerRef.current), []);
  const [volume, setVolume] = useState<number>(() => {
    if (typeof window === "undefined") return 0.5;
    const raw = window.localStorage.getItem(VOLUME_KEY);
    const n = raw == null ? NaN : Number(raw);
    return Number.isFinite(n) && n >= 0 && n <= 1 ? n : 0.5;
  });
  const [muted, setMuted] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(MUTED_KEY) === "1";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(VOLUME_KEY, String(volume));
    } catch {}
  }, [volume]);

  useEffect(() => {
    try {
      window.localStorage.setItem(MUTED_KEY, muted ? "1" : "0");
    } catch {}
  }, [muted]);

  const hasAudio = mode === "audio" || mode === "soundscape";



  return (
    <div className="mx-auto max-w-3xl px-4 py-10 md:py-14">
      <header className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium text-foreground">
          <Shield className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
          Self-guided wellness · Not therapy
        </div>
        <h1 className="font-display text-4xl font-semibold text-foreground md:text-5xl">
          Bilateral Stimulation
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          Gentle left–right rhythms can help the nervous system settle. Use as
          a calming companion — stop any time something feels too much.
        </p>
      </header>

      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {MODES.map((m) => (
          <button
            key={m.id}
            onClick={() => {
              setMode(m.id);
              setPlaying(false);
            }}
            className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 text-xs font-medium transition-all ${
              mode === m.id
                ? "border-gold/60 bg-gradient-to-br from-gold/20 to-accent/20 text-foreground shadow-[var(--shadow-gold)]"
                : "border-border/60 bg-card text-muted-foreground hover:border-gold/40"
            }`}
          >
            <m.icon className="h-5 w-5" />
            {m.label}
          </button>
        ))}
      </div>

      <div className="rounded-3xl border border-border/50 bg-card p-6 md:p-10">
        <p className="mb-6 text-center text-sm text-muted-foreground">
          {MODES.find((m) => m.id === mode)?.blurb}
        </p>

        <div className="relative mx-auto mb-8 flex h-32 max-w-md items-center justify-between rounded-2xl bg-gradient-to-r from-cream/60 via-background to-cream/60 px-4 ring-1 ring-border/40">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">L</span>
          <BilateralVisual mode={mode} playing={playing} speedMs={speedMs} volume={muted ? 0 : volume} />
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">R</span>
        </div>

        <div className="mb-6">
          <label className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>Pace</span>
            <span>{(speedMs / 1000).toFixed(1)}s per side</span>
          </label>
          <input
            type="range"
            min={400}
            max={1600}
            step={50}
            value={speedMs}
            onChange={(e) => setSpeedMs(Number(e.target.value))}
            className="w-full accent-[var(--gold)]"
          />
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>Faster</span>
            <span>Slower</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => {
              // Unlock from the tap itself so iOS arms the shared context.
              if (!playing) claimAudio(ownerRef.current);
              void unlockAudio();
              setPlaying((p) => !p);
            }}
            className="btn-gold"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {playing ? "Pause" : "Begin"}
          </button>
          {hasAudio && (
            <VolumeControl
              value={volume}
              onChange={(v) => {
                setVolume(v);
                if (v > 0) setMuted(false);
              }}
              muted={muted}
              onToggleMute={() => setMuted((m) => !m)}
              ariaLabel="Bilateral audio volume"
            />
          )}
        </div>


        {mode === "butterfly" && playing && (
          <p className="mt-6 text-center font-display text-lg text-foreground">
            Cross your arms · tap left, tap right · breathe slow
          </p>
        )}
      </div>

      <p className="mx-auto mt-8 max-w-xl text-center text-xs text-muted-foreground">
        These tools are educational and self-guided. They are inspired by
        bilateral techniques but are not a replacement for EMDR or trauma
        therapy with a licensed clinician.
      </p>
    </div>
  );
}

function BilateralVisual({
  mode,
  playing,
  speedMs,
  volume = 0.5,
}: {
  mode: Mode;
  playing: boolean;
  speedMs: number;
  volume?: number;
}) {
  const [side, setSide] = useState<"L" | "R">("L");
  const volumeRef = useRef(volume);
  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => setSide((s) => (s === "L" ? "R" : "L")), speedMs);
    return () => clearInterval(t);
  }, [playing, speedMs]);

  // Audio tone — uses the single shared AudioContext. Creating (and closing)
  // a context here used to break audio elsewhere on iOS and left this effect
  // playing into a suspended context.
  useEffect(() => {
    if (!playing || (mode !== "audio" && mode !== "soundscape")) return;
    const ctx = getAudioContext();
    if (!ctx) return;
    resumeAudio();

    // Own alternation counter: the previous code read `side` from a stale
    // closure, so every beep landed on the same channel — no bilateral effect.
    let left = true;
    const playBeep = () => {
      const vol = Math.max(0, Math.min(1, volumeRef.current));
      left = !left;
      if (vol === 0) return;
      const peak = 0.08 * vol;
      const osc = ctx.createOscillator();
      const pannerNode = ctx.createStereoPanner();
      const g = ctx.createGain();
      pannerNode.pan.value = left ? -1 : 1;
      osc.frequency.value = mode === "soundscape" ? 220 : 440;
      osc.type = mode === "soundscape" ? "sine" : "triangle";
      const now = ctx.currentTime;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(peak, now + 0.05);
      g.gain.linearRampToValueAtTime(0, now + speedMs / 1000 - 0.05);
      osc.connect(g).connect(pannerNode).connect(ctx.destination);
      osc.start();
      osc.stop(now + speedMs / 1000);
      osc.onended = () => {
        try { osc.disconnect(); g.disconnect(); pannerNode.disconnect(); } catch { /* noop */ }
      };
    };

    playBeep();
    const i = setInterval(playBeep, speedMs);
    return () => clearInterval(i);
  }, [playing, mode, speedMs]);



  return (
    <div
      className="h-12 w-12 rounded-full bg-gradient-to-br from-[var(--gold-light)] to-[var(--gold)] shadow-[0_0_24px_var(--gold)]"
      style={{
        transition: `transform ${speedMs}ms cubic-bezier(0.45,0,0.55,1)`,
        transform: `translateX(${side === "L" ? "-160px" : "160px"})`,
        opacity: playing ? 1 : 0.4,
      }}
      aria-hidden
    />
  );
}
